/**
 * ows-mail, the Mailbox section's service: the HTTP side of the source switch (see `source.ts`).
 * Contract: `docs/ows-mail/swagger.yml` and `docs/ows-mail/frontend-integration-main.md`.
 *
 * The transport is a direct call to the service's own host and the OWNER'S access token, the way the
 * rest of the app calls its backends: the base URL is `window.config.owsMailApiUrl`, read through
 * `owsMailApiUrl()` from `config/mailboxSource.ts` (the only reader, reached here through
 * `source.ts`), on every call. No proxy answers it and there is
 * no relative fallback: with no base configured the section never gets here (it is unavailable, or on fixtures),
 * and a call that does rejects before any request. The service allows the cross-origin call (CORS,
 * `Authorization` among its allowed headers). Every call carries the signed-in owner's own access token
 * in `Authorization`, raw (no `Bearer`), read from the store fresh on every call so a refreshed token
 * is picked up without a reload, as the affiliates client reads it. (The folder used to say "no token
 * anywhere, on purpose": that was about the service's static shared secret, which had to stay out of
 * the bundle. This client no longer uses it at all; it remains a live credential for the service's
 * internal callers, which is why it must still never be logged or committed. The owner's token is the
 * user's own, already in the browser.) The service resolves it with the owner hub: a
 * `401` means it is missing or no longer resolves, a `403` that the domain is not this owner's.
 *
 * DECISION, not an oversight: these calls use `fetch`, so the global axios refresh-and-teardown
 * interceptor (`src/utils/auth-interceptor.ts`) never sees them. An ows-mail `401` therefore never
 * logs the operator out by accident, and it is also never refreshed and replayed: it is surfaced
 * (the section says to sign in again), not recovered from.
 *
 * Every non-2xx the service answers carries its {@link MailboxError}, and that is what a call
 * rejects with. Anything else (a gateway answering an HTML 502 because the service is down, the
 * network, a missing base URL) rejects with a plain `Error`, so `takeMailboxError` reads it as "not the service" and the
 * UI shows its own line. Bodies are never logged: some carry a password or a sign-in token.
 *
 * Responses are mapped to the section's own shapes at this boundary. The fields the service still
 * names after a former mail provider (its verification block, its account id) stop here.
 */
import {
  billingOfRole,
  isMailboxError,
  MAILBOX_FALLBACK_DOMAIN,
  mailboxDomainReadyFallback,
  type DomainStatus,
  type MailboxDomain,
  type MailboxError,
  type MailboxErrorCode,
  type MailboxPrice,
  type MailboxRecord,
  type MailboxRole,
  type MailboxSso,
  type MailboxStatus,
  type MailboxUsage,
} from '../../../mock-data/mailbox-records';
import { store } from '@/store';
import { owsMailApiUrl } from './source';

type Method = 'GET' | 'POST' | 'DELETE';

/** The service's error, copied field by field so nothing else from the body travels with it. */
const serviceError = (payload: MailboxError): MailboxError => ({
  code: payload.code,
  message: payload.message,
  ...(payload.upstream !== undefined ? { upstream: payload.upstream } : {}),
});

/** The owner's access token, read from the store on EVERY call (never cached at module load), so a
 *  refreshed token is used without a reload. `null` when nobody is signed in. */
const ownerToken = (): string | null => {
  const token = store.getState().authentication.access_token;
  return typeof token === 'string' && token !== '' ? token : null;
};

/** The two refusals the owner-token transport added, keyed by STATUS: the integration doc names their
 *  codes (`unauthorized`, `forbidden`) but the swagger's `Error` schema lists no values, and a gateway
 *  can answer either status with no service body at all, so the status is the stable signal. The
 *  body's `message` is kept when there is one (a `403` is shown in the service's own words). */
const AUTH_REFUSALS: Readonly<Record<number, MailboxErrorCode>> = { 401: 'unauthorized', 403: 'forbidden' };

/**
 * One call. Resolves with the parsed body (`undefined` for a `204`), rejects with the service's
 * {@link MailboxError} or, when the answer is not the service's, a plain `Error` with no body in it.
 * An aborted `signal` drops the call (the rejection is not the service's either). With no owner
 * token there is no call at all: it rejects as `unauthorized`, the same as the service's `401`. With
 * no base URL configured there is no call either: it rejects with a plain `Error`, never guessing a
 * host.
 */
const request = async (method: Method, path: string, body?: object, signal?: AbortSignal): Promise<unknown> => {
  const base = owsMailApiUrl();
  if (base === null) throw new Error('ows-mail: no owsMailApiUrl is configured');
  const token = ownerToken();
  if (token === null) throw { code: 'unauthorized', message: '' } satisfies MailboxError;
  const response = await fetch(`${base}${path}`, {
    method,
    ...(signal ? { signal } : {}),
    headers:
      body === undefined
        ? { Accept: 'application/json', Authorization: token }
        : { Accept: 'application/json', 'Content-Type': 'application/json', Authorization: token },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (response.status === 204) return undefined;
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }
  if (!response.ok) {
    // Read before the status: the contract does not say which status carries it, and a `403` would
    // otherwise turn it into `forbidden`.
    if (isMailboxError(payload) && payload.code === 'not_eligible') throw serviceError(payload);
    const refusal = AUTH_REFUSALS[response.status];
    if (refusal) {
      const message = isJson(payload) && typeof payload.message === 'string' ? payload.message : '';
      throw { code: refusal, message } satisfies MailboxError;
    }
    if (isMailboxError(payload)) throw serviceError(payload);
    throw new Error(`ows-mail: ${method} answered ${response.status} without a service error`);
  }
  return payload;
};

/**
 * The domain's path segment. Refuses the section's display fallback: a brand with no domain on
 * record has no domain at the service, and asking about the placeholder would read (or create) mail
 * for a domain nobody owns.
 */
const domainPath = (domain: string): string => {
  const d = domain.trim().toLowerCase();
  if (!d || d === MAILBOX_FALLBACK_DOMAIN) throw new Error('ows-mail: the brand has no mail domain on record');
  return `/domains/${encodeURIComponent(d)}`;
};

/** A mailbox's path. The address is URL-encoded: it contains an `@`. */
const accountPath = (domain: string, email: string): string =>
  `${domainPath(domain)}/accounts/${encodeURIComponent(email.trim().toLowerCase())}`;

// --- Mapping ---------------------------------------------------------------------------------

type Json = Record<string, unknown>;

const isJson = (value: unknown): value is Json => typeof value === 'object' && value !== null && !Array.isArray(value);

const str = (value: unknown): string | undefined => (typeof value === 'string' ? value : undefined);

/** An ISO date, or `undefined` when absent or not a real date. The service is written in Go, whose
 *  zero time (`0001-01-01T00:00:00Z`) means "never": any year before 1970 is treated as absent, never
 *  formatted as "1 January 1". */
const isoDate = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const time = Date.parse(value);
  return Number.isNaN(time) || new Date(time).getUTCFullYear() < 1970 ? undefined : value;
};
const num = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

/** A body that is not the shape the contract promises: the service answering, but not usably. */
const malformed = (what: string): Error => new Error(`ows-mail: malformed ${what} response`);

const DOMAIN_STATUSES: ReadonlySet<string> = new Set<DomainStatus>([
  'pending',
  'order_created',
  'relay_pending',
  'forward_pending',
  'dns_pending',
  'dns_verified',
  'active',
  'suspended',
  'failed',
]);

/** Where the service keeps the provider's state on a `Domain`: its historical provider name. This is
 *  the one place that name is read; nothing past this module sees it. */
const PROVIDER_STATE_KEY = 'titan';

/** A `Domain`'s provider lookup -> {@link DnsVerification}, or `undefined` when there is no lookup. A
 *  flag that is absent or not `true` reads as not verified. NOT READ by the provisioning screen any more (it paces every row and waits on nothing); kept because the section's domain poll, the only consumer of `fetchDomainWithDns`, still maps it. */
export const toDnsVerification = (domainPayload: unknown): DnsVerification | undefined => {
  if (!isJson(domainPayload)) return undefined;
  const provider = domainPayload[PROVIDER_STATE_KEY];
  const lookup = isJson(provider) ? provider.lastLookup : undefined;
  if (!isJson(lookup)) return undefined;
  return { mxVerified: lookup.mxVerified === true, spfVerified: lookup.spfVerified === true };
};

/** Unknown domain statuses already warned about, so each is logged once per page load. */
const warnedStatuses = new Set<string>();

/** A `Domain`'s status as the section reads it. One it does not know (the service adds states:
 *  `relay_pending` and `forward_pending` arrived unannounced and blanked the section) is read as
 *  `pending`, not ready yet, and warned about once with its value (a status word, never a body). */
const toDomainStatus = (status: string | undefined): DomainStatus => {
  if (status !== undefined && DOMAIN_STATUSES.has(status)) return status as DomainStatus;
  const seen = status ?? '(missing)';
  if (!warnedStatuses.has(seen)) {
    warnedStatuses.add(seen);
    // eslint-disable-next-line no-console -- a contract drift worth seeing, and never a response body
    console.warn(`[ows-mail] unknown domain status "${seen}", read as "pending"`);
  }
  return 'pending';
};

/** `Domain` -> the section's {@link MailboxDomain}. Only what the section reads; the provider's
 *  verification block is dropped here, since the UI is driven by `ready` (whether mail works) and
 *  `status` (what to render while waiting), never by `verified`, which flaps. A domain with no `domain` is refused (there is
 *  nothing to show); one with a status we have not heard of is NOT: it renders as not ready. */
export const toMailboxDomain = (payload: unknown): MailboxDomain => {
  if (!isJson(payload)) throw malformed('domain');
  const domain = str(payload.domain);
  if (!domain) throw malformed('domain');
  const status = toDomainStatus(str(payload.status));
  const mailHost = str(payload.mailHost);
  const failureReason = str(payload.failureReason);
  return {
    domain: domain.toLowerCase(),
    status,
    // Believed when sent; derived from the raw status only when an older deployment omits it.
    ready: typeof payload.ready === 'boolean' ? payload.ready : mailboxDomainReadyFallback(str(payload.status) ?? ''),
    manageDns: payload.manageDns === true,
    ...(mailHost ? { mailHost } : {}),
    ...(failureReason ? { failureReason } : {}),
  };
};

/** What `GET /accounts` says about a mailbox: identity, status, and its slot (`role` and `billing`).
 *  Not the invoice: the service has none. */
export type MailboxAccount = Omit<MailboxRecord, 'invoiceId'>;

const MAILBOX_STATUSES: ReadonlySet<string> = new Set<MailboxStatus>(['active', 'suspended', 'deleted']);

/** `Account` -> {@link MailboxAccount}. The provider's own account id is dropped. A status the
 *  section does not know reads as `suspended`: a pill and nothing to press, rather than a Webmail
 *  button on a mailbox in a state nobody designed for. `role` keeps `system` (the brand's
 *  `support@`); a role it does not know reads as `user`. `billing` is the service's own; an answer
 *  that omits it (an older deployment) gets it derived from `role` by the service's rule. */
export const toMailboxAccount = (payload: unknown, domain: string): MailboxAccount => {
  if (!isJson(payload)) throw malformed('account');
  const email = str(payload.email);
  if (!email) throw malformed('account');
  const status = str(payload.status);
  const passwordSetAt = isoDate(payload.passwordSetAt);
  const role: MailboxRole = payload.role === 'owner' || payload.role === 'system' ? payload.role : 'user';
  const aliases = Array.isArray(payload.aliases)
    ? payload.aliases.filter((a): a is string => typeof a === 'string')
    : undefined;
  return {
    email: email.toLowerCase(),
    domain: (str(payload.domain) ?? domain).toLowerCase(),
    role,
    // Two values. Anything else (absent, or an older deployment's third value) reads by role.
    billing: payload.billing === 'free' || payload.billing === 'paid' ? payload.billing : billingOfRole(role),
    status: status && MAILBOX_STATUSES.has(status) ? (status as MailboxStatus) : 'suspended',
    createdAt: isoDate(payload.createdAt) ?? '',
    passwordRotations: num(payload.passwordRotations) ?? 0,
    ...(passwordSetAt ? { passwordSetAt } : {}),
    ...(aliases && aliases.length > 0 ? { aliases } : {}),
  };
};

/**
 * `MailboxPrice` (the service's) -> the section's {@link MailboxPrice}. THE ONE PLACE THE SERVICE'S
 * NUMBER IS INTERPRETED, and an ASSUMPTION, not a fact the contract states: `amount` is read as
 * MAJOR units of `currency` (10 = 10.00 USD). The contract types it `integer` and says only "in
 * `currency`"; its example answers `{ "amount": 10, "currency": "USD" }`, and it says the same pair
 * is what owner-hub is told to debit, whose wallet amounts this app reads in major units. To be
 * confirmed with the service's author; if it turns out to be cents, this function is the whole fix.
 * Everything past it is major units. The only other unit change is the dev wallet ledger, which
 * counts whole cents and converts once, inside `addMailboxWalletSpent` (`store.ts`).
 *
 * Nothing is guessed: an amount that is not a non-negative integer, or a currency that is not a
 * non-empty string, is a malformed answer, and the section then shows no price at all.
 */
export const toMailboxPrice = (payload: unknown): MailboxPrice => {
  if (!isJson(payload)) throw malformed('price');
  const { amount } = payload;
  const currency = str(payload.currency)?.trim().toUpperCase();
  if (typeof amount !== 'number' || !Number.isInteger(amount) || amount < 0 || !currency) throw malformed('price');
  return { amount, currency };
};

// --- Endpoints -------------------------------------------------------------------------------

/** `GET /domains/{domain}`. Rejects `not_found` for a domain the service has never heard of. */
export const fetchDomain = async (domain: string): Promise<MailboxDomain> =>
  toMailboxDomain(await request('GET', domainPath(domain)));

/** `GET /domains/{domain}` with the provider's DNS lookup as well. The section's domain poll is its
 *  only consumer; the provisioning screen no longer reads the lookup (it paces every row). */
export const fetchDomainWithDns = async (
  domain: string,
): Promise<{ mailDomain: MailboxDomain; dns?: DnsVerification }> => {
  const payload = await request('GET', domainPath(domain));
  const dns = toDnsVerification(payload);
  return { mailDomain: toMailboxDomain(payload), ...(dns ? { dns } : {}) };
};

/** `GET /domains/{domain}/mailbox-price`: what one paid mailbox costs. A price, not a quote: whether
 *  the NEXT mailbox is paid is the list's to say (see `nextMailboxIsPaidByList`). */
export const fetchMailboxPrice = async (domain: string): Promise<MailboxPrice> =>
  toMailboxPrice(await request('GET', `${domainPath(domain)}/mailbox-price`));

/** `GET /domains/{domain}/accounts`, deleted mailboxes excluded (the contract's default). Rejects
 *  `not_found` for an unknown domain, which is not the same answer as `[]`. */
export const fetchAccounts = async (domain: string): Promise<MailboxAccount[]> => {
  const payload = await request('GET', `${domainPath(domain)}/accounts`);
  if (!Array.isArray(payload)) throw malformed('accounts');
  return payload.map((row) => toMailboxAccount(row, domain));
};

/** Whether a rejection is the service's `404`. */
export const isNotFound = (rejection: unknown): boolean => isMailboxError(rejection) && rejection.code === 'not_found';

/**
 * What the section needs on arrival: the domain and its mailboxes, read together, as the contract
 * asks ("fetch both for a domain page": the list alone cannot say whether provisioning happened).
 * A `404` on either is the service never having heard of the domain: `mailDomain: null` and no
 * mailboxes, the section's unknown-domain state (the first create registers it), which renders
 * differently from a registered domain's `200 []`. Anything else rejects.
 */
export const fetchMailboxSection = async (
  domain: string,
): Promise<{ mailDomain: MailboxDomain | null; accounts: MailboxAccount[] }> => {
  const [mailDomain, accounts] = await Promise.all([
    fetchDomain(domain).catch((rejection: unknown) => {
      if (isNotFound(rejection)) return null;
      throw rejection;
    }),
    fetchAccounts(domain).catch((rejection: unknown) => {
      if (isNotFound(rejection)) return [];
      throw rejection;
    }),
  ]);
  return { mailDomain, accounts: mailDomain === null ? [] : accounts };
};

// --- Writes ----------------------------------------------------------------------------------

/** `MailClientSettings`: what a mail client needs, besides the password. Not a secret. */
export type MailClientSettings = {
  username?: string;
  imapHost: string;
  imapPort: number;
  smtpHost: string;
  smtpPort: number;
  security?: string;
};

/** `Credentials`: returned once, by the call that set the password, and stored nowhere. */
export type MailboxCredentials = { email: string; password: string; settings?: MailClientSettings };

/**
 * What the mail provider's own lookup of the zone says, under neutral names: the service reports it
 * under the provider's historical name, which stops here. Only MX and SPF are ever reported; there is
 * no DKIM or DMARC signal, and none is invented.
 */
export type DnsVerification = { mxVerified: boolean; spfVerified: boolean };

/** A create's answer, mapped. `mailDomain` is the domain as the create left it. */
export type CreateMailboxResult = {
  credentials: MailboxCredentials;
  /** What the call actually did: four on a fresh domain, `['mailbox_created']` on one set up already. */
  steps: string[];
  /** Records were published and are propagating. The domain's `dns_pending` status carries it. */
  dnsPending: boolean;
  mailDomain?: MailboxDomain;
  /** The provider's lookup, when the answer carries one. Not read by the provisioning screen any more
   *  (it paces every row); kept as the mapped answer. */
  dns?: DnsVerification;
};

/** `OwnerMailboxRequest`, as the section sends it: the whole request. `manageDns` on the domain's
 *  first mailbox only; `quotaMb` always, in MEGABYTES. `password` is optional in the contract
 *  (omitted, the service generates one); the section always sends its own. The casino, tenant, site
 *  and owner are resolved server-side from the owner's token and are NOT body fields. */
export type CreateMailboxBody = {
  email: string;
  quotaMb: number;
  password?: string;
  manageDns?: true;
};

export const toMailClientSettings = (payload: unknown): MailClientSettings | undefined => {
  if (!isJson(payload)) return undefined;
  const imapHost = str(payload.imapHost);
  const imapPort = num(payload.imapPort);
  const smtpHost = str(payload.smtpHost);
  const smtpPort = num(payload.smtpPort);
  if (!imapHost || imapPort === undefined || !smtpHost || smtpPort === undefined) return undefined;
  const username = str(payload.username);
  const security = str(payload.security);
  return {
    imapHost,
    imapPort,
    smtpHost,
    smtpPort,
    ...(username ? { username } : {}),
    ...(security ? { security } : {}),
  };
};

const toCredentials = (payload: unknown): MailboxCredentials => {
  if (!isJson(payload)) throw malformed('credentials');
  const email = str(payload.email);
  const password = str(payload.password);
  if (!email || !password) throw malformed('credentials');
  const settings = toMailClientSettings(payload.settings);
  return { email: email.toLowerCase(), password, ...(settings ? { settings } : {}) };
};

/** A create's `domain` block, or `undefined` when it is missing or not usable. It is optional, and
 *  the answer around it carries the only copy of a password: an odd `domain` must never reject the
 *  create (the section then derives the domain itself, as the mock source does). */
const optionalDomain = (payload: unknown): MailboxDomain | undefined => {
  try {
    return isJson(payload) ? toMailboxDomain(payload) : undefined;
  } catch {
    return undefined;
  }
};

/** `POST /domains/{domain}/owner-mailbox`. Synchronous: a resolve means the mailbox exists. A `409`
 *  (the address exists) is final for this address; never retry it, a create never returns a new
 *  password for an existing mailbox. `signal` lets the caller stop waiting (see the service's
 *  timeout). */
export const createMailbox = async (
  domain: string,
  body: CreateMailboxBody,
  signal?: AbortSignal,
): Promise<CreateMailboxResult> => {
  const payload = await request('POST', `${domainPath(domain)}/owner-mailbox`, body, signal);
  if (!isJson(payload)) throw malformed('owner-mailbox');
  return {
    credentials: toCredentials(payload.credentials),
    steps: Array.isArray(payload.steps) ? payload.steps.filter((s): s is string => typeof s === 'string') : [],
    dnsPending: payload.dnsPending === true,
    ...optional('mailDomain', optionalDomain(payload.domain)),
    ...optional('dns', toDnsVerification(payload.domain)),
  };
};

/** `POST /domains/{domain}/accounts/{email}/password`. With no `password`, the body is empty and the
 *  service generates one. Signs the owner out of every mail client. */
export const rotatePassword = async (domain: string, email: string, password?: string): Promise<MailboxCredentials> =>
  toCredentials(await request('POST', `${accountPath(domain, email)}/password`, password ? { password } : {}));

/** `DELETE /domains/{domain}/accounts/{email}`. Final from the operator's side (no restore). */
export const deleteMailbox = async (domain: string, email: string): Promise<void> => {
  await request('DELETE', accountPath(domain, email));
};

/** `GET /domains/{domain}/accounts/{email}`: the mail client settings, for the client-setup table on
 *  every visit (the password is never part of it). `undefined` when the answer carries none. */
export const fetchAccountSettings = async (domain: string, email: string): Promise<MailClientSettings | undefined> => {
  const payload = await request('GET', accountPath(domain, email));
  return isJson(payload) ? toMailClientSettings(payload.settings) : undefined;
};

// --- Usage -----------------------------------------------------------------------------------

/** Copy an optional field only when the answer carries it: ABSENT stays absent, never zero. */
const optional = <K extends string, V>(key: K, value: V | undefined): Partial<Record<K, V>> =>
  value === undefined ? {} : ({ [key]: value } as Record<K, V>);

/** A nullable optional: `null` is the contract's own "not known" (it keeps a legitimate `false` or
 *  zero from being omitted), so it is carried as sent. */
const nullable = <T>(value: unknown, is: (v: unknown) => v is T): T | null | undefined =>
  value === null ? null : is(value) ? value : undefined;

const isNum = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);
const isBool = (v: unknown): v is boolean => typeof v === 'boolean';
const isStr = (v: unknown): v is string => typeof v === 'string';

/** `MailboxUsage` -> the section's {@link MailboxUsage}. Nothing is defaulted: a missing `bytesMax`
 *  means no ceiling is known (no storage bar), a missing `unreadMail` means nothing is said. */
export const toMailboxUsage = (payload: unknown): MailboxUsage => {
  if (!isJson(payload)) throw malformed('usage');
  const email = str(payload.email);
  const bytesUsed = num(payload.bytesUsed);
  const messagesUsed = num(payload.messagesUsed);
  if (!email || bytesUsed === undefined || messagesUsed === undefined) throw malformed('usage');
  return {
    email: email.toLowerCase(),
    bytesUsed,
    messagesUsed,
    ...optional('bytesMax', num(payload.bytesMax)),
    ...optional('bytesRemaining', nullable(payload.bytesRemaining, isNum)),
    ...optional('messagesMax', num(payload.messagesMax)),
    ...optional('unreadMail', nullable(payload.unreadMail, isBool)),
    ...optional('lastReceivedAt', payload.lastReceivedAt === null ? null : isoDate(payload.lastReceivedAt)),
    ...optional('smtpSent', num(payload.smtpSent)),
    ...optional('smtpMax', num(payload.smtpMax)),
  };
};

/** `GET /domains/{domain}/accounts/{email}/usage`, read through to the provider on every call (never
 *  cached here). `lastReceived` costs the service a third provider call: only the screen that shows
 *  it asks. */
export const fetchUsage = async (
  domain: string,
  email: string,
  opts: { lastReceived?: boolean } = {},
): Promise<MailboxUsage> =>
  toMailboxUsage(
    await request('GET', `${accountPath(domain, email)}/usage${opts.lastReceived ? '?lastReceived=true' : ''}`),
  );

// --- Webmail ---------------------------------------------------------------------------------

/** `SSOLogin` -> {@link MailboxSso}. A credential: never stored, logged or reused by the caller. */
export const toMailboxSso = (payload: unknown): MailboxSso => {
  if (!isJson(payload)) throw malformed('sso');
  const email = str(payload.email);
  const loginUrl = str(payload.loginUrl);
  const usernameField = str(payload.usernameField);
  const passwordField = str(payload.passwordField);
  const token = str(payload.token);
  if (!email || !loginUrl || !usernameField || !passwordField || !token) throw malformed('sso');
  return {
    email,
    loginUrl,
    usernameField,
    passwordField,
    token,
    ...optional('expiresInHours', num(payload.expiresInHours)),
    ...optional('singleUse', typeof payload.singleUse === 'boolean' ? payload.singleUse : undefined),
  };
};

/** `POST /domains/{domain}/accounts/{email}/sso`: a single-use sign-in, minted per click. A deleted
 *  mailbox (or a provider without single sign-on) answers `409`. */
export const requestSso = async (domain: string, email: string): Promise<MailboxSso> =>
  toMailboxSso(await request('POST', `${accountPath(domain, email)}/sso`));
